import type {
  CreateMemberRequest,
  MemberAddressRequest,
  MemberGuardianRequest,
  MemberHealthRequest,
  MemberMeasurementsRequest,
  MemberProfileDto,
} from "../types";
import { formatCep, normalizeCep } from "./cep";
import { normalizeState } from "./memberValidation";

export interface AddressFormValue {
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface HealthFormValue {
  bloodType: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  notes: string;
}

export interface MeasurementsFormValue {
  weightKg: string;
  heightCm: string;
}

export interface GuardianFormValue {
  key: string;
  id: string | null;
  name: string;
  phone: string;
  address: AddressFormValue;
  notes: string;
}

export interface MemberFormValues {
  name: string;
  cpf: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  planId: string;
  enrollmentType: string;
  enrollmentDate: string;
  address: AddressFormValue;
  health: HealthFormValue;
  measurements: MeasurementsFormValue;
  notes: string;
  guardians: GuardianFormValue[];
}

const ADDRESS_FIELDS: (keyof AddressFormValue)[] = [
  "zipCode",
  "street",
  "number",
  "complement",
  "neighborhood",
  "city",
  "state",
];

let guardianKeySeed = 0;

export function emptyAddressFormValue(): AddressFormValue {
  return { zipCode: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "" };
}

export function createGuardianFormValue(): GuardianFormValue {
  guardianKeySeed += 1;
  return {
    key: `guardian-${guardianKeySeed}`,
    id: null,
    name: "",
    phone: "",
    address: emptyAddressFormValue(),
    notes: "",
  };
}

export function emptyMemberFormValues(): MemberFormValues {
  return {
    name: "",
    cpf: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    planId: "",
    enrollmentType: "",
    enrollmentDate: "",
    address: emptyAddressFormValue(),
    health: { bloodType: "", emergencyContactName: "", emergencyContactPhone: "", notes: "" },
    measurements: { weightKg: "", heightCm: "" },
    notes: "",
    guardians: [],
  };
}

export function memberFormValuesFromProfile(profile: MemberProfileDto): MemberFormValues {
  const address = profile.address ?? emptyAddressFormValue();
  return {
    name: profile.name,
    cpf: profile.cpf,
    email: profile.email ?? "",
    phone: profile.phone ?? "",
    dateOfBirth: profile.dateOfBirth ?? "",
    gender: profile.gender ? String(profile.gender) : "",
    planId: profile.enrollment?.planId ?? "",
    enrollmentType: profile.enrollment ? String(profile.enrollment.type) : "",
    enrollmentDate: profile.enrollment?.enrollmentDate ?? "",
    address: {
      zipCode: formatCep(address.zipCode ?? ""),
      street: address.street ?? "",
      number: address.number ?? "",
      complement: address.complement ?? "",
      neighborhood: address.neighborhood ?? "",
      city: address.city ?? "",
      state: address.state ?? "",
    },
    health: {
      bloodType: profile.health?.bloodType ?? "",
      emergencyContactName: profile.health?.emergencyContactName ?? "",
      emergencyContactPhone: profile.health?.emergencyContactPhone ?? "",
      notes: profile.health?.notes ?? "",
    },
    measurements: {
      weightKg: profile.measurements?.weightKg != null ? String(profile.measurements.weightKg) : "",
      heightCm: profile.measurements?.heightCm != null ? String(profile.measurements.heightCm) : "",
    },
    notes: profile.notes ?? "",
    guardians: profile.guardians.map(guardian => ({
      key: `guardian-${guardian.id}`,
      id: guardian.id,
      name: guardian.name,
      phone: guardian.phone ?? "",
      address: guardian.address
        ? {
            zipCode: guardian.address.zipCode ?? "",
            street: guardian.address.street ?? "",
            number: guardian.address.number ?? "",
            complement: guardian.address.complement ?? "",
            neighborhood: guardian.address.neighborhood ?? "",
            city: guardian.address.city ?? "",
            state: guardian.address.state ?? "",
          }
        : emptyAddressFormValue(),
      notes: guardian.notes ?? "",
    })),
  };
}

function optionalValue(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function filledAddress(address: AddressFormValue): MemberAddressRequest {
  const request: MemberAddressRequest = {};
  for (const field of ADDRESS_FIELDS) {
    const value = optionalValue(address[field]);
    if (value !== undefined) {
      if (field === "state") request[field] = normalizeState(value);
      else if (field === "zipCode") request[field] = normalizeCep(value);
      else request[field] = value;
    }
  }
  return request;
}

function addressRequest(address: AddressFormValue, editing: boolean): MemberAddressRequest | undefined {
  const filled = filledAddress(address);
  if (Object.keys(filled).length > 0) {
    if (!editing) return filled;
    const complete: MemberAddressRequest = {};
    for (const field of ADDRESS_FIELDS) complete[field] = filled[field] ?? null;
    return complete;
  }
  return editing ? {} : undefined;
}

function healthRequest(health: HealthFormValue, editing: boolean): MemberHealthRequest | undefined {
  const filled: MemberHealthRequest = {};
  const bloodType = health.bloodType.trim();
  const emergencyContactName = optionalValue(health.emergencyContactName);
  const emergencyContactPhone = optionalValue(health.emergencyContactPhone);
  const notes = health.notes.trim();
  if (bloodType) filled.bloodType = bloodType;
  if (emergencyContactName) filled.emergencyContactName = emergencyContactName;
  if (emergencyContactPhone) filled.emergencyContactPhone = emergencyContactPhone;
  if (notes) filled.notes = notes;

  if (Object.keys(filled).length > 0) {
    if (!editing) return filled;
    return {
      bloodType: filled.bloodType ?? null,
      emergencyContactName: filled.emergencyContactName ?? null,
      emergencyContactPhone: filled.emergencyContactPhone ?? null,
      notes: filled.notes ?? null,
    };
  }
  return editing ? {} : undefined;
}

function parseNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function measurementsRequest(measurements: MeasurementsFormValue, editing: boolean): MemberMeasurementsRequest | undefined {
  const weightKg = parseNumber(measurements.weightKg);
  const heightCm = parseNumber(measurements.heightCm);
  if (weightKg === null && heightCm === null) return editing ? {} : undefined;
  return { weightKg, heightCm };
}

function guardianRequest(guardian: GuardianFormValue): MemberGuardianRequest {
  const request: MemberGuardianRequest = { name: guardian.name.trim() };
  if (guardian.id) request.id = guardian.id;
  const phone = optionalValue(guardian.phone);
  const notes = guardian.notes.trim();
  if (phone) request.phone = phone;
  if (notes) request.notes = notes;
  const address = filledAddress(guardian.address);
  if (Object.keys(address).length > 0) request.address = address;
  return request;
}

export function buildMemberPayload(values: MemberFormValues, options: { editing: boolean }): CreateMemberRequest {
  const payload: CreateMemberRequest = {
    name: values.name.trim(),
    cpf: values.cpf.trim(),
    dateOfBirth: values.dateOfBirth,
    gender: Number(values.gender),
    enrollment: {
      planId: values.planId,
      type: Number(values.enrollmentType),
      enrollmentDate: values.enrollmentDate,
    },
  };

  const email = optionalValue(values.email);
  const phone = optionalValue(values.phone);
  if (email) payload.email = email;
  if (phone) payload.phone = phone;

  const address = addressRequest(values.address, options.editing);
  const health = healthRequest(values.health, options.editing);
  const measurements = measurementsRequest(values.measurements, options.editing);
  if (address) payload.address = address;
  if (health) payload.health = health;
  if (measurements) payload.measurements = measurements;

  // No PUT, `null` mantém as observações atuais; string vazia limpa o campo.
  const notes = values.notes.trim();
  if (options.editing) payload.notes = notes;
  else if (notes) payload.notes = notes;

  const guardians = values.guardians.map(guardianRequest);
  if (options.editing || guardians.length > 0) payload.guardians = guardians;

  return payload;
}
